// @react
import React, { ReactElement } from 'react'
// @types
import { PropsType as CardProps } from '../../index'
// @libraries
import useResizeObserver from 'use-resize-observer'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'
// @components
import colors from '@bit/atd.web.colors'
import Grid from '@bit/atd.web.grid'
// @card parts
import Wrap from '../../Parts/Wrap'
import Title from '../../Parts/Title'
import Avatar from '../../Parts/Avatar'

export const useStyles = makeStyles({
  root: {
    padding: '16px',
  },
  avatarWrapper: {
    padding: '0 16px 0 0',
  },
  metaDataWrapper: {
    paddingTop: '4px',
    flexGrow: 1,
  },
  userNameWrapper: {
    marginBottom: '4px',
    '& > p': {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '16px',
      margin: '0',
    },
  },
  descriptionWrapper: {
    '& > p': {
      color: colors.black,
      fontSize: '14px',
      lineHeight: '16px',
      margin: 0,
    },
  },
  timestampWrapper: {
    '& > p': {
      color: colors.gray600,
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '16px',
      margin: 0,
      paddingTop: '4px',
    },
  },
  referenceWrapper: {
    marginTop: '16px',
  },
  isHorizontal: {
    padding: '24px',
    '& $referenceWrapper': {
      paddingLeft: '64px',
    },
    '& $timestampWrapper': {
      paddingTop: '0',
    },
  },
})

type PropsType = {
  autoLayoutWidth?: CardProps['autoLayoutWidth']
  avatar: {
    size?: number
    src?: string
    alt?: string
    bgColor?: string
    textAs?: ReactElement | string
  }
  fullName: string
  profileUrl?: string
  description: string | ReactElement
  timestamp?: string
  reference: ReactElement
}

const CardActivity = ({
  autoLayoutWidth,
  avatar,
  fullName,
  profileUrl,
  description,
  timestamp,
  reference,
}: PropsType): ReactElement => {
  const { ref, width } = useResizeObserver<HTMLDivElement>()
  const classes = useStyles()

  const switchToHorizontal = width && width > (autoLayoutWidth || 464)

  let isAutoLayoutHorizontal = false
  if (switchToHorizontal) isAutoLayoutHorizontal = true

  return (
    <Wrap
      ref={ref}
      className={clsx(classes.root, {
        [classes.isHorizontal]: isAutoLayoutHorizontal,
      })}
    >
      <Grid container>
        {/* Avatar */}
        <Grid item className={classes.avatarWrapper}>
          <Avatar
            size={avatar?.size}
            imgSrc={avatar?.src}
            imgAlt={avatar?.alt}
            bgColor={avatar?.bgColor}
          >
            {avatar.textAs && avatar.textAs}
          </Avatar>
        </Grid>

        {/* Meta Data */}
        <Grid item className={classes.metaDataWrapper}>
          {/* User Name */}
          <Grid container direction="row">
            <Grid item className={classes.userNameWrapper}>
              <Typography>
                <Title title={fullName} titleURL={profileUrl} />
              </Typography>
            </Grid>
          </Grid>

          <Grid
            container
            direction="row"
            justify="space-between"
            alignItems="center"
          >
            {/* User Description */}
            <Grid item xs className={classes.descriptionWrapper}>
              <p>{description}</p>
            </Grid>

            {/* Timestamp */}
            {timestamp && (
              <Grid
                item
                xs={isAutoLayoutHorizontal ? 'auto' : 12}
                className={classes.timestampWrapper}
              >
                <p>{timestamp}</p>
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Reference */}
        <Grid xs={12} item className={classes.referenceWrapper}>
          {reference}
        </Grid>
      </Grid>
    </Wrap>
  )
}

export default CardActivity
